/**
 * @fileoverview Name Dialog
 * @description Create or rename a project or scenario.
 */

import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

/**
 * Render the create/edit dialog for a named record.
 * @param {Object} props - Dialog state, labels, initial values, and handlers.
 * @returns {JSX.Element} Dialog.
 */
export function NameDialog({ open, title, nameLabel, initialValues, onCancel, onSubmit }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open) {
      setName(initialValues?.name || '');
      setDescription(initialValues?.description || '');
    }
  }, [initialValues, open]);

  const trimmedName = name.trim();

  /**
   * Submit the dialog when a name is present.
   * @param {React.FormEvent} event - Form submit event.
   * @returns {void}
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    if (trimmedName) {
      onSubmit({ name: trimmedName, description: description.trim() });
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm">
      <form onSubmit={handleSubmit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5} mt={0.5}>
            <TextField
              autoFocus
              required
              fullWidth
              size="small"
              label={nameLabel}
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              size="small"
              label="Description (optional)"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onCancel} color="inherit">Cancel</Button>
          <Button type="submit" variant="contained" disabled={!trimmedName}>Save</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default NameDialog;
